import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiClient from '../config/axios';
import API_CONFIG from '../config/api';

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isResending, setIsResending] = useState(false);
  
  // Get email from navigation state
  const userEmail = location.state?.email;
  
  // Redirect to register if no email provided
  useEffect(() => {
    if (!userEmail) {
      navigate('/register');
    }
  }, [userEmail, navigate]);

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setVerificationCode(value);
      if (error) setError(''); // Clear error when user starts typing
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.AUTH.VERIFY_EMAIL, {
        code: verificationCode,
        email: userEmail
      });

      if (response.status === 200) {
        // Redirect to login page
        navigate('/login');
      }
    } catch (error) {
      console.error('Verification error:', error);
      if (error.response) {
        setError(error.response.data?.message || 'Invalid verification code. Please try again.');
      } else if (error.request) {
        setError('Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError('');

    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.AUTH.RESEND_VERIFICATION_CODE, {
        email: userEmail
      });
      
      if (response.status === 200) {
        setError('');
      }
    } catch (error) {
      console.error('Resend code error:', error);
      setError('Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="questionnaire-page">
      <div className="page-header">
        <div className="page-info">
          <h2>Verify Your Email</h2>
          <p className="page-description">
            We've sent a 6-digit verification code to your email address. Please enter it below to verify your account.
          </p>
        </div>
      </div>

      <div className="questions-container">
        <div className="question-block">
          <form onSubmit={handleVerify}>
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="verificationCode">Verification Code *</label>
              <input
                type="text"
                id="verificationCode"
                name="verificationCode"
                value={verificationCode}
                onChange={handleCodeChange}
                placeholder="Enter 6-digit code"
                className={error ? 'error' : ''}
                maxLength="6"
                required
              />
              <div className="verification-hint">
                <p>Enter the 6-digit code sent to your email address</p>
              </div>
            </div>

            <button
              type="submit"
              className="submit-btn"
              disabled={isLoading || verificationCode.length !== 6}
            >
              {isLoading ? 'Verifying...' : 'Verify Email'}
            </button>
          </form>

          <div className="auth-links">
            <p>
              Didn't receive the code?{' '}
              <button 
                type="button" 
                className="link-button"
                onClick={handleResendCode}
                disabled={isResending}
              >
                {isResending ? 'Sending...' : 'Resend code'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
